import React, { useState, useEffect, useRef } from "react";
import styles from "../Tree.module.css";

interface TreeNode {
  id: number;
  value: number;
  left: TreeNode | null;
  right: TreeNode | null;
  x?: number;
  y?: number;
}

interface BSTEngineProps {
  externalNodes?: number[];
  traversal: string | null;
  setHighlightLine: React.Dispatch<React.SetStateAction<number | null>>;
  highlightedNodes: number[];
  setHighlightedNodes: React.Dispatch<React.SetStateAction<number[]>>;
  resetTrigger?: boolean;
  generateTrigger?: number;
  setTraversalComplete?: React.Dispatch<React.SetStateAction<boolean>>;
  insertValue?: number | null;
  deleteValue?: number | null;
}

const BSTEngine: React.FC<BSTEngineProps> = ({
  externalNodes,
  traversal,
  setHighlightLine,
  highlightedNodes,
  setHighlightedNodes,
  resetTrigger,
  generateTrigger,
  setTraversalComplete,
  insertValue,
  deleteValue,
}) => {
  const [root, setRoot] = useState<TreeNode | null>(null);
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const animationTimers = useRef<NodeJS.Timeout[]>([]);

  const insertNode = (node: TreeNode | null, value: number): TreeNode => {
    if (!node) return { id: value, value, left: null, right: null };
    if (value < node.value) node.left = insertNode(node.left, value);
    else if (value > node.value) node.right = insertNode(node.right, value);
    return node;
  };

  const deleteNode = (node: TreeNode | null, value: number): TreeNode | null => {
    if (!node) return null;
    if (value < node.value) node.left = deleteNode(node.left, value);
    else if (value > node.value) node.right = deleteNode(node.right, value);
    else {
      if (!node.left) return node.right;
      if (!node.right) return node.left;
      let successor = node.right;
      while (successor.left) successor = successor.left;
      node.value = successor.value;
      node.right = deleteNode(node.right, successor.value);
    }
    return node;
  };

  const getTreeDepth = (node: TreeNode | null): number => {
    if (!node) return 0;
    return 1 + Math.max(getTreeDepth(node.left), getTreeDepth(node.right));
  };

  const assignPositions = (
    node: TreeNode | null,
    depth = 0,
    minX = -400,
    maxX = 400,
    levelHeight = 100
  ): TreeNode[] => {
    if (!node) return [];
    const midX = (minX + maxX) / 2;
    const y = depth * levelHeight + 60;
    node.x = midX;
    node.y = y;
    return [
      node,
      ...assignPositions(node.left, depth + 1, minX, midX, levelHeight),
      ...assignPositions(node.right, depth + 1, midX, maxX, levelHeight),
    ];
  };

  const buildTreeFromValues = (values: number[]) => {
    let newRoot: TreeNode | null = null;
    values.forEach((v) => {
      newRoot = insertNode(newRoot, v);
    });
    setRoot(newRoot);
    setNodes(assignPositions(newRoot, 0, -400, 400));
  };

  const generateRandomTree = () => {
    const values = Array.from({ length: 7 }, () =>
      Math.floor(Math.random() * 50) + 1
    );
    buildTreeFromValues(values);
  };

  useEffect(() => {
    if (generateTrigger !== undefined && generateTrigger > 0) {
      if (externalNodes && externalNodes.length > 0)
        buildTreeFromValues(externalNodes);
      else generateRandomTree();
      return;
    }

    if (!root && (!generateTrigger || generateTrigger === 0)) {
      if (externalNodes && externalNodes.length > 0)
        buildTreeFromValues(externalNodes);
      else generateRandomTree();
    }
  }, [generateTrigger, externalNodes]);

  useEffect(() => {
    if (!resetTrigger) return;
    animationTimers.current.forEach(clearTimeout);
    animationTimers.current = [];
    setHighlightedNodes([]);
    setHighlightLine(null);
  }, [resetTrigger]);

  useEffect(() => {
    if (!root || !traversal) return;

    animationTimers.current.forEach(clearTimeout);
    animationTimers.current = [];
    setHighlightedNodes([]);
    setHighlightLine(null);
    if (setTraversalComplete) setTraversalComplete(false);

    const order: TreeNode[] = [];

    const dfs = (node: TreeNode | null, type: string) => {
      if (!node) return;
      if (type === "preorder") order.push(node);
      dfs(node.left, type);
      if (type === "inorder") order.push(node);
      dfs(node.right, type);
      if (type === "postorder") order.push(node);
    };

    const bfs = (node: TreeNode | null) => {
      const queue: TreeNode[] = [];
      if (node) queue.push(node);
      while (queue.length > 0) {
        const current = queue.shift()!;
        order.push(current);
        if (current.left) queue.push(current.left);
        if (current.right) queue.push(current.right);
      }
    };

    if (traversal === "levelorder") bfs(root);
    else dfs(root, traversal);

    order.forEach((node, idx) => {
      const timer = setTimeout(() => {
        setHighlightedNodes((prev) => [...prev, node.value]);
        setHighlightLine(idx % 5);
        if (idx === order.length - 1 && setTraversalComplete)
          setTraversalComplete(true);
      }, idx * 700);
      animationTimers.current.push(timer);
    });

    const cleanup = setTimeout(() => {
      setHighlightLine(null);
    }, order.length * 700 + 500);
    animationTimers.current.push(cleanup);

    return () => {
      animationTimers.current.forEach(clearTimeout);
      animationTimers.current = [];
    };
  }, [traversal, root]);

  const depth = getTreeDepth(root);
  const svgHeight = Math.max(450, depth * 120); 
  const viewBoxHeight = Math.max(400, depth * 120); 

  return (
    <div className={styles.treeVizContainer}>
      <svg width="100%" height={svgHeight} viewBox={`-600 0 1200 ${viewBoxHeight}`}>

        {nodes.map((node) => (
          <g key={`edges-${node.id}`}>
            {node.left && (
              <line
                x1={node.x}
                y1={node.y}
                x2={node.left.x}
                y2={node.left.y}
                className={styles.treeLine}
              />
            )}
            {node.right && (
              <line
                x1={node.x}
                y1={node.y}
                x2={node.right.x}
                y2={node.right.y}
                className={styles.treeLine}
              />
            )}
          </g>
        ))}

        {nodes.map((node) => (
          <g key={`node-${node.id}`}>
            <circle
              cx={node.x}
              cy={node.y}
              r="25"
              className={`${styles.treeNode} ${
                highlightedNodes.includes(node.value)
                  ? styles.highlighted
                  : ""
              }`}
            />
            <text x={node.x} y={node.y! + 5} className={styles.nodeLabel}>
              {node.value}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default BSTEngine;
